import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FirebaseError } from 'firebase/app';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { cn } from '../lib/utils';
import KodaCareLogoFull from '../components/Custom Icons/KodaCareLogoFull';
import { Mail } from 'lucide-react';
import { useLanguage } from '../functions/useLanguage';
import AppButton from '../components/AppButton';
import { getPendingInvite } from '../lib/pendingInvite';

export default function VerifyEmail() {
  const { firebaseUser, resendVerificationEmail, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await resendVerificationEmail();
      setResent(true);
    } catch (err) {
      if (err instanceof FirebaseError) {
        setError(`${t('verify.errorResend')} (${err.code})`);
      } else {
        setError(t('verify.errorResend'));
      }
    } finally {
      setResending(false);
    }
  };

  const handleBackToSignIn = async () => {
    const pending = getPendingInvite();
    if (pending) {
      await logout({ redirectTo: null });
      navigate(`/login?continueUrl=${encodeURIComponent(pending)}`);
      return;
    }
    await logout({ redirectTo: null });
    navigate('/login');
  };

  const email = firebaseUser?.email ?? '';

  return (
    <div className={cn('min-h-screen flex flex-col items-center justify-center p-4', 'bg-background-light')}>
      <div className={cn('w-full max-w-md rounded-3xl overflow-hidden', 'bg-background-light')}>
        {/* Header — same blue band as Login */}
        <div className={cn('p-12 text-center', 'bg-background-light')}>
          <div className="w-72 h-auto flex items-center justify-center mx-auto mb-0">
            <KodaCareLogoFull height={55} className="w-full h-auto text-primary-brown" />
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Icon + heading */}
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 bg-primary-brown/10 rounded-full flex items-center justify-center mb-4">
              <Mail size={30} className="text-primary-brown" />
            </div>
            <h2 className={cn('text-xl font-bold mb-2', 'text-text-primary')}>{t('verify.title')}</h2>
            <p className={cn('text-sm leading-relaxed', 'text-text-secondary')}>
              {t('verify.subtitle').replace('{email}', email || '—')}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-error-red\/5 border border-error-red\/20 text-error-red text-sm">
              {error}
            </div>
          )}

          {resent && (
            <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200 text-positive-green text-sm">
              {t('verify.resent')}
            </div>
          )}

          <div className="space-y-3">
            {/* Resend button — only shown while signed in (i.e. just registered) */}
            {firebaseUser && !firebaseUser.emailVerified && (
              <AppButton
                onClick={handleResend}
                disabled={resending || resent}
                className="p-4"
              >
                {resending ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : resent ? (
                  t('verify.emailSent')
                ) : (
                  t('verify.resend')
                )}
              </AppButton>
            )}

            {/* Back to Sign In */}
            <AppButton
              variant="secondary"
              onClick={handleBackToSignIn}
              className="p-3 font-medium"
            >
              {t('verify.backToSignIn')}
            </AppButton>
          </div>

          <p className={cn('text-center text-xs mt-6', 'text-gray-400')}>
            {t('verify.spamHint')}
          </p>
        </div>
      </div>
    </div>
  );
}
